import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Col,
    Divider,
    Form,
    Input,
    message,
    Modal,
    Progress,
    Row,
    Select,
    Space,
    Steps,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
    Upload,
} from 'antd';
import {
    ArrowLeftOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    DatabaseOutlined,
    FileTextOutlined,
    ImportOutlined,
    InfoCircleOutlined,
    PlusOutlined,
    SettingOutlined,
    UploadOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { UploadProps } from 'antd';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import '../style/MeasurementImport.css';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

// ─── Types ───────────────────────────────────────────────────────────────────

interface DeviceItem {
    id: number;
    label?: string | null;
    name?: string | null;
    serialId?: string | null;
}

interface AreaItem {
    id: number;
    name: string;
}

type ColumnRole =
    | 'ignore'
    | 'latitude'
    | 'longitude'
    | 'measured_at'
    | 'measurement_code'
    | 'depth_index'
    | `value:${string}`;

interface ColumnMapping {
    index: number;
    header: string;
    role: ColumnRole;
    /** used when role starts with "value:" — the valueType string */
    valueType?: string;
}

interface ParsedRow {
    [colIndex: number]: string;
}

interface PreviewMeasurement {
    rowIndex: number;
    latitude: number | null;
    longitude: number | null;
    measuredAt: string | null;
    measurementCode: string | null;
    values: { valueType: string; numValue: number | null; textValue: string | null }[];
    valid: boolean;
    validationErrors: string[];
}

/** Represents multiple rows from the CSV that share the same lat/lon, grouped into one measurement */
interface GroupedMeasurement {
    locationKey: string;
    locationIndex: number;
    latitude: number;
    longitude: number;
    measuredAt: string | null;
    measurementCode: string | null;
    /** Unique sorted depth/index values found for this location */
    depths: string[];
    /** All value entries, each tagged with a depth */
    values: { valueType: string; depth: string; numValue: number | null; textValue: string | null }[];
    valid: boolean;
    validationErrors: string[];
}

type ImportStep = 'upload' | 'mapping' | 'ai_analysis' | 'assignment' | 'review';

const STEP_ORDER: ImportStep[] = ['upload', 'mapping', 'assignment', 'review'];
const AI_STEP_ORDER: ImportStep[] = ['upload', 'ai_analysis', 'assignment', 'review'];

const DELIMITERS = [
    { label: 'Comma  ( , )', value: ',' },
    { label: 'Tab  ( \\t )', value: '\t' },
    { label: 'Semicolon  ( ; )', value: ';' },
    { label: 'Space  ( )', value: ' ' },
    { label: 'Pipe  ( | )', value: '|' },
    { label: 'Custom…', value: '__custom__' },
];

const RESERVED_ROLES: { label: string; value: ColumnRole }[] = [
    { label: 'Ignore this column', value: 'ignore' },
    { label: 'Latitude', value: 'latitude' },
    { label: 'Longitude', value: 'longitude' },
    { label: 'Date / Time (measuredAt)', value: 'measured_at' },
    { label: 'Measurement Code', value: 'measurement_code' },
    { label: '📏 Depth / Index Axis', value: 'depth_index' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const detectDelimiter = (text: string): string => {
    const candidates = [',', '\t', ';', '|'];
    const firstLine = text.split('\n')[0] || '';
    let best = ',';
    let bestCount = 0;
    for (const c of candidates) {
        const count = firstLine.split(c).length - 1;
        if (count > bestCount) {
            bestCount = count;
            best = c;
        }
    }
    return best;
};

const parseCSV = (text: string, delimiter: string, skipRows: number): { headers: string[]; rows: ParsedRow[] } => {
    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    if (lines.length === 0) return { headers: [], rows: [] };

    const dataLines = lines.slice(skipRows).filter((l) => l.trim() !== '');
    if (dataLines.length === 0) return { headers: [], rows: [] };

    // Determine headers from first kept line (or auto-generate)
    const firstLine = dataLines[0].split(delimiter);
    const headers = firstLine.map((h, i) => h.trim() || `Column ${i + 1}`);

    const rows: ParsedRow[] = dataLines.slice(1).map((line) => {
        const cells = line.split(delimiter);
        const row: ParsedRow = {};
        cells.forEach((cell, idx) => {
            row[idx] = cell.trim();
        });
        return row;
    });

    return { headers, rows };
};

const tryParseNumber = (v: string): number | null => {
    if (!v || v.trim() === '') return null;
    const n = Number(v.trim().replace(',', '.'));
    return Number.isFinite(n) ? n : null;
};

const tryParseDate = (v: string): string | null => {
    if (!v) return null;
    // Try common formats
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
    return null;
};

// ─── Component ───────────────────────────────────────────────────────────────

const MeasurementImport: React.FC = () => {
    const navigate = useNavigate();

    // ── Import mode ── ('manual' = existing CSV wizard, 'ai' = AI-assisted)
    const [importMode, setImportMode] = useState<'manual' | 'ai'>('manual');

    // Step
    const [currentStep, setCurrentStep] = useState<ImportStep>('upload');
    const activeStepOrder = importMode === 'ai' ? AI_STEP_ORDER : STEP_ORDER;
    const currentStepIndex = activeStepOrder.indexOf(currentStep);

    // AI-specific state
    const [aiLoading, setAiLoading] = useState(false);
    const [aiExplanation, setAiExplanation] = useState('');
    const [aiMeasurements, setAiMeasurements] = useState<any[]>([]);
    const [aiChatHistory, setAiChatHistory] = useState<{ role: 'user' | 'ai'; text: string }[]>([]);
    const [aiChatInput, setAiChatInput] = useState('');
    const [aiFileContent, setAiFileContent] = useState<string>('');
    const [aiFileImages, setAiFileImages] = useState<string[]>([]);
    const [aiFileName, setAiFileName] = useState<string>('');
    const [aiTruncated, setAiTruncated] = useState(false);
    const [aiParseError, setAiParseError] = useState<string | null>(null);
    const [aiChunkProgress, setAiChunkProgress] = useState<{ current: number; total: number; rows: number } | null>(null);

    // Upload / Parse state
    const [rawText, setRawText] = useState<string>('');
    const [fileName, setFileName] = useState<string>('');
    const [delimiter, setDelimiter] = useState<string>(',');
    const [customDelimiter, setCustomDelimiter] = useState<string>('');
    const [skipRows, setSkipRows] = useState<number>(1);
    const [parsedHeaders, setParsedHeaders] = useState<string[]>([]);
    const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
    /** Values from the absolute first line of the file, before any skipping — typically the descriptor/header row */
    const [rawFirstRow, setRawFirstRow] = useState<string[]>([]);

    // Column Mapping state
    const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
    const [customValueTypes, setCustomValueTypes] = useState<string[]>([]);
    const [newValueTypeName, setNewValueTypeName] = useState<string>('');
    /** When true, rows sharing the same lat/lon are merged into one measurement using the depth_index column as the dimension */
    const [groupByLocation, setGroupByLocation] = useState<boolean>(false);

    // Assignment state
    const [devices, setDevices] = useState<DeviceItem[]>([]);
    const [areas, setAreas] = useState<AreaItem[]>([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState<number | null>(null);
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
    const [codePrefix, setCodePrefix] = useState<string>('MANUAL_IMPORT');
    const [loadingDevices, setLoadingDevices] = useState(false);
    const [loadingAreas, setLoadingAreas] = useState(false);

    // Review / Import state
    const [importing, setImporting] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [importResult, setImportResult] = useState<{ created: number; failed: number; errors: { row: number; error: string }[] } | null>(null);

    // ── Derived ──────────────────────────────────────────────────────────────

    const effectiveDelimiter = delimiter === '__custom__' ? customDelimiter : delimiter;

    const previewRows = useMemo(() => parsedRows.slice(0, 10), [parsedRows]);

    const preview: PreviewMeasurement[] = useMemo(() => {
        if (!parsedRows.length || !columnMappings.length) return [];

        const latCol = columnMappings.find((c) => c.role === 'latitude');
        const lonCol = columnMappings.find((c) => c.role === 'longitude');
        const dateCol = columnMappings.find((c) => c.role === 'measured_at');
        const codeCol = columnMappings.find((c) => c.role === 'measurement_code');
        const valueCols = columnMappings.filter((c) => (c.role as string).startsWith('value:'));

        return parsedRows.slice(0, 100).map((row, i) => {
            const errors: string[] = [];
            const lat = latCol ? tryParseNumber(row[latCol.index]) : null;
            const lon = lonCol ? tryParseNumber(row[lonCol.index]) : null;
            const measuredAt = dateCol ? tryParseDate(row[dateCol.index]) : null;
            const measurementCode = codeCol ? (row[codeCol.index] || null) : null;

            if (!lat || !lon) errors.push('Missing or invalid latitude/longitude');
            if (dateCol && !measuredAt) errors.push('Could not parse date/time');

            const values = valueCols.map((col) => {
                const vt = col.role.slice(6); // remove "value:"
                const raw = row[col.index];
                const numValue = tryParseNumber(raw);
                return {
                    valueType: vt,
                    numValue,
                    textValue: numValue === null ? (raw || null) : null,
                };
            });

            return {
                rowIndex: i + 1,
                latitude: lat,
                longitude: lon,
                measuredAt,
                measurementCode,
                values,
                valid: errors.length === 0,
                validationErrors: errors,
            };
        });
    }, [parsedRows, columnMappings]);

    const validCount = useMemo(() => preview.filter((r) => r.valid).length, [preview]);
    const invalidCount = useMemo(() => preview.filter((r) => !r.valid).length, [preview]);
    const totalRows = parsedRows.length;

    /** The column mapped as depth/index axis, if any */
    const depthIndexCol = useMemo(() => columnMappings.find((c) => c.role === 'depth_index'), [columnMappings]);
    const hasDepthColumn = !!depthIndexCol;

    /** When groupByLocation is active, groups rows by lat/lon into one measurement per location with depth-indexed values */
    const groupedPreview = useMemo<GroupedMeasurement[]>(() => {
        if (!groupByLocation || !parsedRows.length || !columnMappings.length) return [];

        const latCol = columnMappings.find((c) => c.role === 'latitude');
        const lonCol = columnMappings.find((c) => c.role === 'longitude');
        const dateCol = columnMappings.find((c) => c.role === 'measured_at');
        const codeCol = columnMappings.find((c) => c.role === 'measurement_code');
        const valueCols = columnMappings.filter((c) => (c.role as string).startsWith('value:'));

        const groups = new Map<string, GroupedMeasurement>();
        let locationCounter = 0;

        parsedRows.forEach((row) => {
            const lat = latCol ? tryParseNumber(row[latCol.index]) : null;
            const lon = lonCol ? tryParseNumber(row[lonCol.index]) : null;
            if (!lat || !lon) return;

            const depthRaw = depthIndexCol ? (row[depthIndexCol.index] ?? '') : '';
            const locationKey = `${lat.toFixed(8)}_${lon.toFixed(8)}`;

            if (!groups.has(locationKey)) {
                const measuredAt = dateCol ? tryParseDate(row[dateCol.index]) : null;
                const measurementCode = codeCol ? (row[codeCol.index] || null) : null;
                groups.set(locationKey, {
                    locationKey,
                    locationIndex: locationCounter++,
                    latitude: lat,
                    longitude: lon,
                    measuredAt,
                    measurementCode,
                    depths: [],
                    values: [],
                    valid: true,
                    validationErrors: [],
                });
            }

            const group = groups.get(locationKey)!;
            if (depthRaw && !group.depths.includes(depthRaw)) {
                group.depths.push(depthRaw);
            }

            valueCols.forEach((col) => {
                const vt = col.role.slice(6);
                const raw = row[col.index];
                const numValue = tryParseNumber(raw);
                group.values.push({
                    valueType: vt,
                    depth: depthRaw,
                    numValue,
                    textValue: numValue === null ? (raw || null) : null,
                });
            });
        });

        // Sort depths numerically when possible
        groups.forEach((group) => {
            group.depths.sort((a, b) => {
                const na = Number(a), nb = Number(b);
                return (Number.isFinite(na) && Number.isFinite(nb)) ? na - nb : a.localeCompare(b);
            });
        });

        return Array.from(groups.values());
    }, [groupByLocation, parsedRows, columnMappings, depthIndexCol]);

    const groupedValidCount = useMemo(() => groupedPreview.filter((g) => g.valid).length, [groupedPreview]);
    const groupedInvalidCount = useMemo(() => groupedPreview.filter((g) => !g.valid).length, [groupedPreview]);

    // Active counts depending on mode
    const activeImportCount = importMode === 'ai' ? aiMeasurements.length : (groupByLocation ? groupedValidCount : validCount);

    // ── Effects ──────────────────────────────────────────────────────────────

    // Re-parse whenever delimiter or skipRows changes
    useEffect(() => {
        if (!rawText) return;
        const d = delimiter === '__custom__' ? customDelimiter : delimiter;
        if (!d) return;
        const { headers, rows } = parseCSV(rawText, d, skipRows);
        setParsedHeaders(headers);
        setParsedRows(rows);

        // Always capture the absolute first line of the raw file as descriptor hints
        const rawLines = rawText.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
        const firstLineValues = (rawLines[0] || '').split(d).map((c) => c.trim());
        setRawFirstRow(firstLineValues);

        // Reset column mappings when headers change
        setColumnMappings(
            headers.map((h, i) => ({
                index: i,
                header: h,
                role: guessRole(h, i),
            }))
        );
    }, [rawText, delimiter, customDelimiter, skipRows]);

    // Load devices and areas when on assignment step
    useEffect(() => {
        if (currentStep !== 'assignment') return;
        if (devices.length === 0) fetchDevices();
        if (areas.length === 0) fetchAreas();
    }, [currentStep]);

    // ── Helpers ──────────────────────────────────────────────────────────────

    const guessRole = (header: string, index: number): ColumnRole => {
        const h = header.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (h.includes('lat')) return 'latitude';
        if (h.includes('lon') || h.includes('lng')) return 'longitude';
        if (h.includes('date') || h.includes('time') || h.includes('timestamp') || h.includes('measuredat')) return 'measured_at';
        if (h.includes('code') || h.includes('id')) return 'measurement_code';
        if (h === 'depth' || h === 'z' || h === 'level' || h.includes('depth')) return 'depth_index';
        return 'ignore';
    };

    const fetchDevices = useCallback(async () => {
        setLoadingDevices(true);
        try {
            const res = await axios.get(ENDPOINTS.GET_DEVICES, { withCredentials: true });
            const data: DeviceItem[] = res.data?.data || res.data || [];
            setDevices(data);
        } catch (e) {
            console.error('Failed to load devices', e);
        } finally {
            setLoadingDevices(false);
        }
    }, []);

    const fetchAreas = useCallback(async () => {
        setLoadingAreas(true);
        try {
            const res = await axios.get(ENDPOINTS.AREAS_V2, { withCredentials: true });
            const data: AreaItem[] = res.data?.data || res.data || [];
            setAreas(data);
        } catch (e) {
            console.error('Failed to load areas', e);
        } finally {
            setLoadingAreas(false);
        }
    }, []);

    // ── File Upload ───────────────────────────────────────────────────────────

    const handleFileRead = useCallback((file: File) => {
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            if (!text) return;
            setRawText(text);
            const detected = detectDelimiter(text);
            setDelimiter(detected);
        };
        reader.readAsText(file);
        return false; // prevent antd from uploading
    }, []);

    const uploadProps: UploadProps = {
        accept: '.csv,.txt,.tsv',
        multiple: false,
        beforeUpload: handleFileRead,
        showUploadList: false,
    };

    // ── Column Mapping ────────────────────────────────────────────────────────

    const setRoleForColumn = useCallback((index: number, role: ColumnRole) => {
        setColumnMappings((prev) =>
            prev.map((m) => (m.index === index ? { ...m, role, valueType: role.startsWith('value:') ? role.slice(6) : undefined } : m))
        );
    }, []);

    const addCustomValueType = useCallback(() => {
        const name = newValueTypeName.trim().replace(/\s+/g, '_').toLowerCase();
        if (!name) return;
        if (customValueTypes.includes(name)) {
            message.warning(`Value type "${name}" already exists`);
            return;
        }
        setCustomValueTypes((prev) => [...prev, name]);
        setNewValueTypeName('');
    }, [newValueTypeName, customValueTypes]);

    const valueTypeOptions = useMemo(() => {
        const builtIn = ['moisture', 'firmness', 'ndvi', 'coverage', 'compaction', 'temperature', 'ph', 'nitrogen', 'phosphorus', 'potassium'];
        return [...new Set([...builtIn, ...customValueTypes])];
    }, [customValueTypes]);

    // ── Import ────────────────────────────────────────────────────────────────

    const doImport = useCallback(async () => {
        if (!selectedDeviceId) {
            message.error('Please select a device');
            return;
        }

        setImporting(true);
        setImportProgress(0);
        setImportResult(null);

        const results = { created: 0, failed: 0, errors: [] as { row: number; error: string }[] };
        const BATCH_SIZE = 20;

        if (importMode === 'ai') {
            // ── AI mode: aiMeasurements are already structured by GPT ────────
            const toImport = aiMeasurements;
            if (!toImport.length) {
                message.error('No AI measurements to import');
                setImporting(false);
                return;
            }
            for (let i = 0; i < toImport.length; i += BATCH_SIZE) {
                const batch = toImport.slice(i, i + BATCH_SIZE);
                const payload = batch.map((m: any, batchIdx: number) => ({
                    deviceId: selectedDeviceId,
                    areaId: selectedAreaId || null,
                    measurementCode: m.measurementCode || `${codePrefix}_${String(i + batchIdx + 1).padStart(4, '0')}`,
                    measuredAt: m.measuredAt || null,
                    geom: (m.latitude != null && m.longitude != null)
                        ? { type: 'Point', coordinates: [m.longitude, m.latitude] }
                        : null,
                    values: (m.values || []).map((v: any) => ({
                        valueType: v.valueType,
                        numValue: v.numValue ?? null,
                        textValue: v.textValue ?? null,
                        meta: v.meta || {},
                        processingStage: v.processingStage || 'processed',
                    })),
                }));
                try {
                    const res = await axios.post(ENDPOINTS.MEASUREMENTS_IMPORT, { measurements: payload }, { withCredentials: true });
                    const br = res.data?.data;
                    results.created += br?.created ?? batch.length;
                    results.failed += br?.failed ?? 0;
                    if (br?.errors?.length) br.errors.forEach((e: any) => results.errors.push({ row: i + e.index + 1, error: e.error }));
                } catch (err: any) {
                    results.failed += batch.length;
                    results.errors.push({ row: i + 1, error: err.response?.data?.message || err.message });
                }
                setImportProgress(Math.round(((i + batch.length) / toImport.length) * 100));
            }
            setImportResult(results);
            setImporting(false);
            if (results.created > 0) message.success(`Successfully imported ${results.created} measurements`);
            if (results.failed > 0) message.warning(`${results.failed} rows failed to import`);
            return;
        }

        if (groupByLocation) {
            // ── Grouped mode: one measurement per unique lat/lon ──────────────
            const toImport = groupedPreview.filter((g) => g.valid);
            if (!toImport.length) {
                message.error('No valid grouped measurements to import');
                setImporting(false);
                return;
            }

            for (let i = 0; i < toImport.length; i += BATCH_SIZE) {
                const batch = toImport.slice(i, i + BATCH_SIZE);

                const payload = batch.map((g, batchIdx) => {
                    const code = g.measurementCode || `${codePrefix}_${String(i + batchIdx + 1).padStart(4, '0')}`;
                    // Normalised axis label from the depth_index column header (e.g. "depth", "level", "z")
                    const axisHeader = (depthIndexCol?.header ?? 'depth')
                        .trim().toLowerCase().replace(/\s+/g, '_');
                    // One MeasurementValue per unique value type — profile stored as x/y parallel arrays
                    // valueType = "{measurement}_{axis}",  meta = { x: axisLabel, y: measurementLabel }
                    // textValue = JSON: { "x": [10, 20, 30], "y": [20.0, 25.0, 30.0] }
                    const vtypes = [...new Set(g.values.map((v) => v.valueType))];
                    const values = vtypes.map((vt) => {
                        const entries = g.values
                            .filter((v) => v.valueType === vt)
                            .sort((a, b) => {
                                const na = Number(a.depth), nb = Number(b.depth);
                                return (Number.isFinite(na) && Number.isFinite(nb))
                                    ? na - nb
                                    : String(a.depth).localeCompare(String(b.depth));
                            });
                        return {
                            valueType: `${vt}_${axisHeader}`,
                            numValue: null as number | null,
                            textValue: JSON.stringify({
                                x: entries.map((e) => (Number.isFinite(Number(e.depth)) ? Number(e.depth) : e.depth)),
                                y: entries.map((e) => e.numValue ?? e.textValue ?? null),
                            }),
                            meta: { x: axisHeader, y: vt },
                            processingStage: 'processed',
                        };
                    });
                    return {
                        deviceId: selectedDeviceId,
                        areaId: selectedAreaId || null,
                        measurementCode: code,
                        measuredAt: g.measuredAt || null,
                        geom: { type: 'Point', coordinates: [g.longitude, g.latitude] },
                        values,
                    };
                });

                try {
                    const res = await axios.post(ENDPOINTS.MEASUREMENTS_IMPORT, { measurements: payload }, { withCredentials: true });
                    const br = res.data?.data;
                    results.created += br?.created ?? batch.length;
                    results.failed += br?.failed ?? 0;
                    if (br?.errors?.length) br.errors.forEach((e: any) => results.errors.push({ row: i + e.index + 1, error: e.error }));
                } catch (err: any) {
                    results.failed += batch.length;
                    results.errors.push({ row: i + 1, error: err.response?.data?.message || err.message });
                }

                setImportProgress(Math.round(((i + batch.length) / toImport.length) * 100));
            }
        } else {
            // ── Flat mode: one measurement per row ───────────────────────────
            const toImport = preview.filter((r) => r.valid);
            if (!toImport.length) {
                message.error('No valid rows to import');
                setImporting(false);
                return;
            }

            for (let i = 0; i < toImport.length; i += BATCH_SIZE) {
                const batch = toImport.slice(i, i + BATCH_SIZE);

                const payload = batch.map((r, batchIdx) => {
                    const code = r.measurementCode || `${codePrefix}_${String(i + batchIdx + 1).padStart(4, '0')}`;
                    return {
                        deviceId: selectedDeviceId,
                        areaId: selectedAreaId || null,
                        measurementCode: code,
                        measuredAt: r.measuredAt || null,
                        geom: r.latitude && r.longitude ? { type: 'Point', coordinates: [r.longitude, r.latitude] } : null,
                        values: r.values.map((v) => ({
                            valueType: v.valueType,
                            numValue: v.numValue,
                            textValue: v.textValue,
                            processingStage: 'processed',
                        })),
                    };
                });

                try {
                    const res = await axios.post(ENDPOINTS.MEASUREMENTS_IMPORT, { measurements: payload }, { withCredentials: true });
                    const br = res.data?.data;
                    results.created += br?.created ?? batch.length;
                    results.failed += br?.failed ?? 0;
                    if (br?.errors?.length) br.errors.forEach((e: any) => results.errors.push({ row: i + e.index + 1, error: e.error }));
                } catch (err: any) {
                    results.failed += batch.length;
                    results.errors.push({ row: i + 1, error: err.response?.data?.message || err.message });
                }

                setImportProgress(Math.round(((i + batch.length) / toImport.length) * 100));
            }
        }

        setImportResult(results);
        setImporting(false);

        if (results.created > 0) message.success(`Successfully imported ${results.created} measurements`);
        if (results.failed > 0) message.warning(`${results.failed} rows failed to import`);
    }, [groupByLocation, groupedPreview, preview, selectedDeviceId, selectedAreaId, codePrefix]);

    // ── Navigation ────────────────────────────────────────────────────────────

    // ── AI file handling ─────────────────────────────────────────────────────

    const handleAiFileRead = useCallback((file: File) => {
        setAiFileName(file.name);
        const isImage = file.type.startsWith('image/');
        if (isImage) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const dataUrl = e.target?.result as string;
                if (dataUrl) setAiFileImages([dataUrl]);
            };
            reader.readAsDataURL(file);
        } else {
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target?.result as string;
                if (text) setAiFileContent(text.slice(0, 50000));
            };
            reader.readAsText(file);
        }
        return false;
    }, []);

    const aiUploadProps: UploadProps = {
        accept: '*',
        multiple: false,
        beforeUpload: handleAiFileRead,
        showUploadList: false,
    };

    /** Maximum rows per AI chunk — small enough to guarantee complete JSON output */
    const AI_CHUNK_ROWS = 50;

    const handleAiParse = useCallback(async () => {
        if (!aiFileContent && aiFileImages.length === 0) {
            message.error('Please upload a file first');
            return;
        }
        setAiLoading(true);
        setAiTruncated(false);
        setAiParseError(null);
        setAiChunkProgress(null);

        // ── Images: single call (can't split an image) ─────────────────────
        if (aiFileImages.length > 0 || !aiFileContent) {
            try {
                const res = await axios.post(
                    ENDPOINTS.MEASUREMENTS_AI_PARSE,
                    { content: aiFileContent || null, images: aiFileImages.length > 0 ? aiFileImages : undefined },
                    { withCredentials: true }
                );
                const data = res.data?.data;
                const measurements = data?.measurements || [];
                const explanation = data?.explanation || 'File analysed. Ready for review.';
                setAiMeasurements(measurements);
                setAiExplanation(explanation);
                setAiTruncated(data?.truncated === true);
                setAiParseError(data?.parseError || null);
                setAiChatHistory([{ role: 'ai', text: `${explanation} (${measurements.length} measurements found)` }]);
                setCurrentStep('ai_analysis');
            } catch (err: any) {
                message.error('AI parsing failed: ' + (err.response?.data?.message || err.message));
            } finally {
                setAiLoading(false);
                setAiChunkProgress(null);
            }
            return;
        }

        // ── Text file: split into chunks ───────────────────────────────────
        const lines = aiFileContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
        const nonEmpty = lines.filter((l) => l.trim() !== '');
        const headerLine = nonEmpty[0] || '';          // first line = header / column names
        const dataLines = nonEmpty.slice(1);           // remaining = data rows

        // If file is small enough, send in one shot
        if (dataLines.length <= AI_CHUNK_ROWS) {
            try {
                const res = await axios.post(
                    ENDPOINTS.MEASUREMENTS_AI_PARSE,
                    { content: aiFileContent },
                    { withCredentials: true }
                );
                const data = res.data?.data;
                const measurements = data?.measurements || [];
                const explanation = data?.explanation || 'File analysed. Ready for review.';
                setAiMeasurements(measurements);
                setAiExplanation(explanation);
                setAiTruncated(data?.truncated === true);
                setAiParseError(data?.parseError || null);
                setAiChatHistory([{ role: 'ai', text: `${explanation} (${measurements.length} measurements found)` }]);
                setCurrentStep('ai_analysis');
            } catch (err: any) {
                message.error('AI parsing failed: ' + (err.response?.data?.message || err.message));
            } finally {
                setAiLoading(false);
                setAiChunkProgress(null);
            }
            return;
        }

        // ── Large file: chunked processing ─────────────────────────────────
        const chunks: string[][] = [];
        for (let i = 0; i < dataLines.length; i += AI_CHUNK_ROWS) {
            chunks.push(dataLines.slice(i, i + AI_CHUNK_ROWS));
        }

        const allMeasurements: any[] = [];
        let firstExplanation = '';
        let anyParseError: string | null = null;

        try {
            for (let ci = 0; ci < chunks.length; ci++) {
                setAiChunkProgress({ current: ci + 1, total: chunks.length, rows: chunks[ci].length });
                const chunkContent = [headerLine, ...chunks[ci]].join('\n');
                const res = await axios.post(
                    ENDPOINTS.MEASUREMENTS_AI_PARSE,
                    { content: chunkContent },
                    { withCredentials: true }
                );
                const data = res.data?.data;
                const chunkMeasurements: any[] = data?.measurements || [];
                allMeasurements.push(...chunkMeasurements);
                if (!firstExplanation && data?.explanation) firstExplanation = data.explanation;
                if (!anyParseError && data?.parseError) anyParseError = data.parseError;
            }

            const explanation = firstExplanation || 'File analysed via chunked processing. Ready for review.';
            setAiMeasurements(allMeasurements);
            setAiExplanation(explanation);
            setAiTruncated(false); // chunked = all rows processed, no truncation
            setAiParseError(anyParseError);
            setAiChatHistory([{
                role: 'ai',
                text: `${explanation} — processed ${chunks.length} chunks, ${allMeasurements.length} measurements extracted from ${dataLines.length} rows.`
            }]);
            setCurrentStep('ai_analysis');
        } catch (err: any) {
            message.error('AI parsing failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setAiLoading(false);
            setAiChunkProgress(null);
        }
    }, [aiFileContent, aiFileImages]);

    const handleAiRefine = useCallback(async () => {
        const msg = aiChatInput.trim();
        if (!msg) return;
        setAiChatInput('');
        setAiChatHistory((prev) => [...prev, { role: 'user', text: msg }]);
        setAiLoading(true);
        try {
            const res = await axios.post(
                ENDPOINTS.MEASUREMENTS_AI_REFINE,
                {
                    message: msg,
                    content: aiFileContent || null,
                    images: aiFileImages.length > 0 ? aiFileImages : undefined,
                    currentMeasurements: aiMeasurements,
                },
                { withCredentials: true }
            );
            const data = res.data?.data;
            const updated = data?.measurements ?? aiMeasurements;
            const explanation = data?.explanation || 'Done.';
            const truncated = data?.truncated === true;
            const parseError = data?.parseError || null;
            setAiMeasurements(updated);
            setAiTruncated(truncated);
            setAiParseError(parseError || null);
            setAiChatHistory((prev) => [...prev, { role: 'ai', text: `${explanation} (${updated.length} measurements)` }]);
        } catch (err: any) {
            setAiChatHistory((prev) => [...prev, { role: 'ai', text: 'Error: ' + (err.response?.data?.message || err.message) }]);
        } finally {
            setAiLoading(false);
        }
    }, [aiChatInput, aiFileContent, aiFileImages, aiMeasurements]);

    // ── Navigation ────────────────────────────────────────────────────────────

    const canProceedFromUpload = importMode === 'ai'
        ? (aiFileContent.length > 0 || aiFileImages.length > 0)
        : (rawText.length > 0 && parsedRows.length > 0);
    const canProceedFromMapping = columnMappings.some((c) => c.role === 'latitude') && columnMappings.some((c) => c.role === 'longitude');
    const canProceedFromAssignment = selectedDeviceId !== null;

    const goNext = () => {
        // In AI mode, upload → ai_analysis triggers the AI parse, not a simple step advance
        if (importMode === 'ai' && currentStep === 'upload') {
            handleAiParse();
            return;
        }
        const next = activeStepOrder[currentStepIndex + 1];
        if (next) setCurrentStep(next);
    };

    const goBack = () => {
        const prev = activeStepOrder[currentStepIndex - 1];
        if (prev) setCurrentStep(prev);
    };

    // ── Render: Step 1 ────────────────────────────────────────────────────────

    const renderUploadStep = () => {
        if (importMode === 'ai') {
            return (
                <div className="mi-step-content">
                    <Alert
                        type="info"
                        showIcon
                        icon={<span style={{ fontSize: 18 }}>🤖</span>}
                        message="Drop any file — CSV, TXT, Excel export, image, screenshot of a table, photo of a data sheet, etc. The AI will extract measurement data automatically."
                        style={{ marginBottom: 16 }}
                    />
                    <Dragger {...aiUploadProps} className="mi-dragger mi-dragger-ai">
                        <p className="ant-upload-drag-icon">
                            <UploadOutlined style={{ fontSize: 40, color: '#722ed1' }} />
                        </p>
                        <p className="ant-upload-text">Drop any file — CSV, image, screenshot, PDF export…</p>
                        <p className="ant-upload-hint">Images are analysed with Vision AI · Text files sent as content · All file types welcome</p>
                    </Dragger>
                    {aiFileName && (
                        <Card size="small" style={{ marginTop: 16 }} title={aiLoading && aiChunkProgress ? '⏳ Processing…' : 'Ready to Analyse'}>
                            <Space wrap>
                                <Tag icon={<FileTextOutlined />} color="purple">{aiFileName}</Tag>
                                {aiFileImages.length > 0 && <Tag color="blue">🖼 Image detected — Vision AI will be used</Tag>}
                                {aiFileContent && <Tag color="green">📄 Text content — {aiFileContent.length.toLocaleString()} chars</Tag>}
                                {/* Show chunk count for large files */}
                                {aiFileContent.length > 0 && (() => {
                                    const nonEmpty = aiFileContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter((l) => l.trim() !== '');
                                    const totalChunks = Math.ceil(Math.max(0, nonEmpty.length - 1) / AI_CHUNK_ROWS);
                                    return totalChunks > 1 ? (
                                        <Tag color="orange">📦 Will process in {totalChunks} chunks ({AI_CHUNK_ROWS} rows each)</Tag>
                                    ) : null;
                                })()}
                            </Space>
                            {/* Chunk progress bar — shown while chunked processing is running */}
                            {aiLoading && aiChunkProgress && (
                                <div style={{ marginTop: 12 }}>
                                    <Space style={{ marginBottom: 6 }}>
                                        <Text style={{ fontSize: 13 }}>
                                            🤖 Processing chunk {aiChunkProgress.current} of {aiChunkProgress.total} ({aiChunkProgress.rows} rows)…
                                        </Text>
                                    </Space>
                                    <Progress
                                        percent={Math.round((aiChunkProgress.current / aiChunkProgress.total) * 100)}
                                        status="active"
                                        strokeColor="#722ed1"
                                        size="small"
                                    />
                                </div>
                            )}
                            {/* Large file info — now shows chunked mode, not a warning */}
                            {!aiLoading && aiFileContent.length > 25000 && (
                                <Alert
                                    type="info"
                                    showIcon
                                    style={{ marginTop: 10 }}
                                    message={`Large file (${(aiFileContent.length / 1000).toFixed(0)} KB) — will be split into ${Math.ceil(Math.max(0, aiFileContent.replace(/\r\n/g,'\n').replace(/\r/g,'\n').split('\n').filter(l=>l.trim()!=='').length - 1) / AI_CHUNK_ROWS)} chunks of ${AI_CHUNK_ROWS} rows each. All rows will be processed.`}
                                />
                            )}
                        </Card>
                    )}
                </div>
            );
        }

        return (
        <div className="mi-step-content">
            <Dragger {...uploadProps} className="mi-dragger">
                <p className="ant-upload-drag-icon">
                    <UploadOutlined style={{ fontSize: 40, color: '#1677ff' }} />
                </p>
                <p className="ant-upload-text">Click or drag a CSV / TXT file here</p>
                <p className="ant-upload-hint">Supports .csv, .txt, .tsv files. All parsing is done locally in your browser.</p>
            </Dragger>

            {rawText && (
                <div className="mi-upload-settings">
                    <Card size="small" title={<Space><SettingOutlined /> Parse Settings</Space>}>
                        <Row gutter={[16, 16]} align="middle">
                            <Col xs={24} sm={8}>
                                <div className="mi-field-label">File</div>
                                <Tag icon={<FileTextOutlined />} color="blue">{fileName}</Tag>
                            </Col>
                            <Col xs={24} sm={8}>
                                <div className="mi-field-label">Column Separator</div>
                                <Select
                                    value={delimiter}
                                    onChange={setDelimiter}
                                    style={{ width: '100%' }}
                                >
                                    {DELIMITERS.map((d) => (
                                        <Option key={d.value} value={d.value}>{d.label}</Option>
                                    ))}
                                </Select>
                                {delimiter === '__custom__' && (
                                    <Input
                                        style={{ marginTop: 8 }}
                                        placeholder="Enter custom delimiter"
                                        maxLength={3}
                                        value={customDelimiter}
                                        onChange={(e) => setCustomDelimiter(e.target.value)}
                                    />
                                )}
                            </Col>
                            <Col xs={24} sm={8}>
                                <div className="mi-field-label">Skip first N rows (headers / titles)</div>
                                <Select
                                    value={skipRows}
                                    onChange={setSkipRows}
                                    style={{ width: '100%' }}
                                >
                                    {[0, 1, 2, 3, 4, 5].map((n) => (
                                        <Option key={n} value={n}>{n === 0 ? 'None' : `${n} row${n > 1 ? 's' : ''}`}</Option>
                                    ))}
                                </Select>
                            </Col>
                        </Row>
                    </Card>

                    {parsedRows.length > 0 && (
                        <Card
                            size="small"
                            className="mi-preview-card"
                            title={<Space><InfoCircleOutlined /> Data Preview (first 10 rows)</Space>}
                            extra={<Tag color="green">{parsedRows.length} data rows detected</Tag>}
                        >
                            <div className="mi-table-scroll">
                                <Table
                                    size="small"
                                    dataSource={previewRows.map((r, i) => ({ ...r, _key: i }))}
                                    rowKey="_key"
                                    pagination={false}
                                    scroll={{ x: true }}
                                    columns={parsedHeaders.map((h, colIdx) => ({
                                        title: <Tooltip title={`Column ${colIdx + 1}`}>{h}</Tooltip>,
                                        dataIndex: colIdx,
                                        key: colIdx,
                                        ellipsis: true,
                                        width: 120,
                                    }))}
                                />
                            </div>
                        </Card>
                    )}
                </div>
            )}
        </div>
        );
    };

    // ── Render: Step 2 ────────────────────────────────────────────────────────

    const renderMappingStep = () => (
        <div className="mi-step-content">
            <Alert
                type="info"
                showIcon
                message="Map each column to its data role. Latitude and Longitude are required. Value columns will be stored as measurement values."
                style={{ marginBottom: 16 }}
            />

            <Card size="small" title="Column Roles" className="mi-mapping-card">
                <Table
                    size="small"
                    dataSource={columnMappings}
                    rowKey="index"
                    pagination={false}
                    columns={[
                        {
                            title: 'Column Header',
                            dataIndex: 'header',
                            render: (h: string, rec: ColumnMapping) => {
                                const firstRowValue = rawFirstRow[rec.index];
                                const hasDifferentFirstRow = firstRowValue && firstRowValue !== h;
                                return (
                                    <Space direction="vertical" size={2}>
                                        {/* First row value is the primary descriptor — show it bold */}
                                        <Text strong className="mi-col-descriptor">
                                            {hasDifferentFirstRow ? firstRowValue : h}
                                        </Text>
                                        {/* Parsed header shown as secondary label only when different */}
                                        {hasDifferentFirstRow && (
                                            <Tooltip title="Column name derived after skipping rows">
                                                <Text type="secondary" style={{ fontSize: 11 }}>
                                                    {h}
                                                </Text>
                                            </Tooltip>
                                        )}
                                    </Space>
                                );
                            },
                        },
                        {
                            title: 'Sample Values',
                            key: 'sample',
                            render: (_: any, rec: ColumnMapping) => {
                                const samples = parsedRows.slice(0, 3).map((r) => r[rec.index]).filter(Boolean);
                                return <Text type="secondary" style={{ fontSize: 12 }}>{samples.join(' / ')}</Text>;
                            },
                        },
                        {
                            title: 'Assign Role',
                            key: 'role',
                            width: 280,
                            render: (_: any, rec: ColumnMapping) => (
                                <Select
                                    value={rec.role}
                                    style={{ width: '100%' }}
                                    onChange={(val: ColumnRole) => setRoleForColumn(rec.index, val)}
                                    dropdownRender={(menu) => (
                                        <>
                                            {menu}
                                            <Divider style={{ margin: '4px 0' }} />
                                            <div style={{ padding: '4px 8px' }}>
                                                <Space>
                                                    <Input
                                                        size="small"
                                                        placeholder="New value type…"
                                                        value={newValueTypeName}
                                                        onChange={(e) => setNewValueTypeName(e.target.value)}
                                                        onPressEnter={addCustomValueType}
                                                        style={{ width: 160 }}
                                                    />
                                                    <Button size="small" icon={<PlusOutlined />} onClick={addCustomValueType}>Add</Button>
                                                </Space>
                                            </div>
                                        </>
                                    )}
                                >
                                    {RESERVED_ROLES.map((r) => (
                                        <Option key={r.value} value={r.value}>{r.label}</Option>
                                    ))}
                                    <Select.OptGroup label="Value Types">
                                        {valueTypeOptions.map((vt) => (
                                            <Option key={`value:${vt}`} value={`value:${vt}`}>
                                                📊 {vt}
                                            </Option>
                                        ))}
                                    </Select.OptGroup>
                                </Select>
                            ),
                        },
                    ]}
                />
            </Card>

            {/* Validation summary */}
            <Card size="small" className="mi-mapping-summary" title="Mapping Summary">
                <Row gutter={[8, 8]}>
                    {columnMappings.some((c) => c.role === 'latitude') ? (
                        <Col><Tag icon={<CheckCircleOutlined />} color="success">Latitude mapped</Tag></Col>
                    ) : (
                        <Col><Tag icon={<WarningOutlined />} color="error">Latitude missing</Tag></Col>
                    )}
                    {columnMappings.some((c) => c.role === 'longitude') ? (
                        <Col><Tag icon={<CheckCircleOutlined />} color="success">Longitude mapped</Tag></Col>
                    ) : (
                        <Col><Tag icon={<WarningOutlined />} color="error">Longitude missing</Tag></Col>
                    )}
                    {columnMappings.some((c) => c.role === 'measured_at') && (
                        <Col><Tag icon={<CheckCircleOutlined />} color="success">Date/Time mapped</Tag></Col>
                    )}
                    {columnMappings.some((c) => c.role === 'measurement_code') && (
                        <Col><Tag icon={<CheckCircleOutlined />} color="success">Measurement Code mapped</Tag></Col>
                    )}
                    {hasDepthColumn && (
                        <Col><Tag icon={<CheckCircleOutlined />} color="purple">📏 Depth column: {depthIndexCol!.header}</Tag></Col>
                    )}
                    {columnMappings.filter((c) => (c.role as string).startsWith('value:')).map((c) => (
                        <Col key={c.index}><Tag icon={<CheckCircleOutlined />} color="blue">Value: {c.role.slice(6)}</Tag></Col>
                    ))}
                </Row>

                {/* Group by location toggle — shown when depth column is assigned */}
                {hasDepthColumn && (
                    <div className="mi-group-toggle">
                        <Space align="center">
                            <Switch
                                checked={groupByLocation}
                                onChange={setGroupByLocation}
                                size="small"
                            />
                            <Text strong>Group rows by same Lat/Lon into one measurement</Text>
                            <Tooltip title="Rows with identical coordinates will be merged into a single measurement. Each value column will create depth-indexed entries (e.g. moisture_depth_10, moisture_depth_20).">
                                <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                            </Tooltip>
                        </Space>
                        {groupByLocation && parsedRows.length > 0 && (
                            <Alert
                                type="success"
                                showIcon
                                style={{ marginTop: 10 }}
                                message={
                                    <span>
                                        <strong>{groupedPreview.length}</strong> unique locations found from{' '}
                                        <strong>{totalRows}</strong> rows
                                        {depthIndexCol && groupedPreview.length > 0 && (
                                            <span> · depths: <strong>{[...new Set(groupedPreview.flatMap((g) => g.depths))].sort((a, b) => { const na = Number(a), nb = Number(b); return (Number.isFinite(na) && Number.isFinite(nb)) ? na - nb : a.localeCompare(b); }).join(', ')}</strong></span>
                                        )}
                                        {' '}· value types will be stored as <em>{columnMappings.filter((c) => (c.role as string).startsWith('value:')).slice(0, 2).map((c) => `${c.role.slice(6)}_depth_X`).join(', ')}{columnMappings.filter((c) => (c.role as string).startsWith('value:')).length > 2 ? ', …' : ''}</em>
                                    </span>
                                }
                            />
                        )}
                    </div>
                )}
            </Card>
        </div>
    );

    // ── Render: Step 3 ────────────────────────────────────────────────────────

    const renderAssignmentStep = () => (
        <div className="mi-step-content">
            <Card size="small" title={<Space><DatabaseOutlined /> Assign to Device &amp; Area</Space>}>
                <Form layout="vertical">
                    <Form.Item
                        label={<span>Device <span style={{ color: 'red' }}>*</span></span>}
                        help="Every measurement must belong to a device"
                    >
                        <Select
                            showSearch
                            placeholder="Select a device…"
                            loading={loadingDevices}
                            value={selectedDeviceId}
                            onChange={(v) => setSelectedDeviceId(v)}
                            filterOption={(input, option) =>
                                String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            style={{ width: '100%' }}
                            notFoundContent={loadingDevices ? 'Loading…' : 'No devices found'}
                        >
                            {devices.map((d) => (
                                <Option key={d.id} value={d.id}>
                                    {d.label || d.name || `Device #${d.id}`}
                                    {d.serialId ? ` (${d.serialId})` : ''}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item label="Area (optional)" help="Assign all imported measurements to an area">
                        <Select
                            showSearch
                            allowClear
                            placeholder="Select an area (optional)…"
                            loading={loadingAreas}
                            value={selectedAreaId}
                            onChange={(v) => setSelectedAreaId(v ?? null)}
                            filterOption={(input, option) =>
                                String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            style={{ width: '100%' }}
                        >
                            {areas.map((a) => (
                                <Option key={a.id} value={a.id}>{a.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Measurement Code Prefix"
                        help="Used for rows where no Measurement Code column was mapped. Will produce e.g. MANUAL_IMPORT_0001"
                    >
                        <Input
                            value={codePrefix}
                            onChange={(e) => setCodePrefix(e.target.value.toUpperCase().replace(/\s+/g, '_'))}
                            placeholder="MANUAL_IMPORT"
                            maxLength={40}
                        />
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );

    // ── Render: Step 4 ────────────────────────────────────────────────────────

    const reviewColumns: ColumnsType<PreviewMeasurement> = [
        {
            title: 'Row',
            dataIndex: 'rowIndex',
            width: 60,
        },
        {
            title: 'Latitude',
            dataIndex: 'latitude',
            width: 110,
            render: (v: number | null) => v !== null ? <Text>{v.toFixed(6)}</Text> : <Tag color="error">missing</Tag>,
        },
        {
            title: 'Longitude',
            dataIndex: 'longitude',
            width: 110,
            render: (v: number | null) => v !== null ? <Text>{v.toFixed(6)}</Text> : <Tag color="error">missing</Tag>,
        },
        {
            title: 'Date/Time',
            dataIndex: 'measuredAt',
            width: 160,
            render: (v: string | null) => v ? <Text style={{ fontSize: 12 }}>{new Date(v).toLocaleString()}</Text> : <Tag>—</Tag>,
        },
        {
            title: 'Code',
            dataIndex: 'measurementCode',
            render: (v: string | null) => v || <Tag color="blue">Auto-generated</Tag>,
        },
        {
            title: 'Values',
            dataIndex: 'values',
            render: (vals: any[]) => (
                <Space wrap>
                    {vals.map((v, i) => (
                        <Tag key={i} color="geekblue">{v.valueType}: {v.numValue ?? v.textValue ?? '—'}</Tag>
                    ))}
                </Space>
            ),
        },
        {
            title: 'Status',
            key: 'status',
            width: 90,
            render: (_: any, rec: PreviewMeasurement) =>
                rec.valid ? (
                    <Tag icon={<CheckCircleOutlined />} color="success">Valid</Tag>
                ) : (
                    <Tooltip title={rec.validationErrors.join(', ')}>
                        <Tag icon={<CloseCircleOutlined />} color="error">Invalid</Tag>
                    </Tooltip>
                ),
        },
    ];

    const renderReviewStep = () => {
        const activeValid = groupByLocation ? groupedValidCount : validCount;
        const activeInvalid = groupByLocation ? groupedInvalidCount : invalidCount;

        return (
        <div className="mi-step-content">
            {!importResult ? (
                <>
                    <Row gutter={16} className="mi-review-summary">
                        <Col xs={24} sm={8}>
                            <Card size="small" className="mi-summary-card mi-summary-total">
                                <div className="mi-summary-number">{groupByLocation ? groupedPreview.length : totalRows}</div>
                                <div className="mi-summary-label">{groupByLocation ? 'Grouped Locations' : 'Total Rows in File'}</div>
                            </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Card size="small" className="mi-summary-card mi-summary-valid">
                                <div className="mi-summary-number">{activeValid}</div>
                                <div className="mi-summary-label">{groupByLocation ? 'Locations to import' : 'Valid Rows (will be imported)'}</div>
                            </Card>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Card size="small" className="mi-summary-card mi-summary-invalid">
                                <div className="mi-summary-number">{groupByLocation ? totalRows - groupedPreview.length : activeInvalid}</div>
                                <div className="mi-summary-label">{groupByLocation ? 'Rows without coordinates (skipped)' : 'Invalid Rows (will be skipped)'}</div>
                            </Card>
                        </Col>
                    </Row>

                    {!groupByLocation && activeInvalid > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            message={`${activeInvalid} rows have validation errors and will be skipped. Hover the "Invalid" tag for details.`}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    {groupByLocation ? (
                        /* ── Grouped view ── */
                        <Card
                            size="small"
                            title={<Space><span>📍 Grouped Measurement Preview</span><Tag color="purple">{groupedPreview.length} locations</Tag></Space>}
                            className="mi-review-card"
                        >
                            <div className="mi-table-scroll">
                                <Table
                                    size="small"
                                    dataSource={groupedPreview}
                                    rowKey="locationKey"
                                    pagination={{ pageSize: 20, showSizeChanger: false }}
                                    scroll={{ x: true }}
                                    columns={[
                                        {
                                            title: 'Location',
                                            key: 'loc',
                                            width: 200,
                                            render: (_: any, g: GroupedMeasurement) => (
                                                <Space direction="vertical" size={0}>
                                                    <Text style={{ fontSize: 12 }}>{g.latitude.toFixed(6)}, {g.longitude.toFixed(6)}</Text>
                                                    {g.measurementCode && <Tag color="blue" style={{ fontSize: 10 }}>{g.measurementCode}</Tag>}
                                                </Space>
                                            ),
                                        },
                                        {
                                            title: 'Depths',
                                            key: 'depths',
                                            width: 140,
                                            render: (_: any, g: GroupedMeasurement) => (
                                                <Space wrap size={2}>
                                                    {g.depths.map((d) => <Tag key={d} color="geekblue" style={{ fontSize: 11 }}>{d}</Tag>)}
                                                </Space>
                                            ),
                                        },
                                        {
                                            title: 'Values per depth',
                                            key: 'vals',
                                            render: (_: any, g: GroupedMeasurement) => {
                                                // Show unique value types with their per-depth values
                                                const vtypes = [...new Set(g.values.map((v) => v.valueType))];
                                                return (
                                                    <Space direction="vertical" size={2}>
                                                        {vtypes.map((vt) => {
                                                            const entries = g.values.filter((v) => v.valueType === vt);
                                                            return (
                                                                <Space key={vt} size={2} wrap>
                                                                    <Text type="secondary" style={{ fontSize: 11, minWidth: 70 }}>{vt}:</Text>
                                                                    {entries.map((e, i) => (
                                                                        <Tag key={i} style={{ fontSize: 10 }}>{e.depth ? `@${e.depth}→` : ''}{e.numValue ?? e.textValue ?? '—'}</Tag>
                                                                    ))}
                                                                </Space>
                                                            );
                                                        })}
                                                    </Space>
                                                );
                                            },
                                        },
                                        {
                                            title: 'Date/Time',
                                            dataIndex: 'measuredAt',
                                            width: 140,
                                            render: (v: string | null) => v ? <Text style={{ fontSize: 11 }}>{new Date(v).toLocaleString()}</Text> : <Tag>—</Tag>,
                                        },
                                    ]}
                                />
                            </div>
                        </Card>
                    ) : (
                        /* ── Flat view ── */
                        <Card
                            size="small"
                            title="Preview (first 100 rows)"
                            className="mi-review-card"
                        >
                            <div className="mi-table-scroll">
                                <Table
                                    size="small"
                                    dataSource={preview}
                                    rowKey="rowIndex"
                                    columns={reviewColumns}
                                    pagination={{ pageSize: 20, showSizeChanger: false }}
                                    scroll={{ x: true }}
                                    rowClassName={(rec) => rec.valid ? '' : 'mi-row-invalid'}
                                />
                            </div>
                        </Card>
                    )}

                    {importing && (
                        <Card size="small" style={{ marginTop: 16 }}>
                            <Space direction="vertical" style={{ width: '100%' }}>
                                <Text>Importing measurements…</Text>
                                <Progress percent={importProgress} status="active" />
                            </Space>
                        </Card>
                    )}
                </>
            ) : (
                <Card size="small" title="Import Complete">
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 16, padding: '4px 12px' }}>
                            {importResult.created} measurements created
                        </Tag>
                        {importResult.failed > 0 && (
                            <Tag icon={<CloseCircleOutlined />} color="error" style={{ fontSize: 16, padding: '4px 12px' }}>
                                {importResult.failed} rows failed
                            </Tag>
                        )}
                        {importResult.errors.length > 0 && (
                            <div>
                                <Text type="secondary" style={{ fontSize: 12 }}>Errors:</Text>
                                {importResult.errors.slice(0, 10).map((e, i) => (
                                    <div key={i}><Text type="danger" style={{ fontSize: 12 }}>Row {e.row}: {e.error}</Text></div>
                                ))}
                            </div>
                        )}
                        <Space style={{ marginTop: 12 }}>
                            <Button type="primary" onClick={() => navigate('/measurements')}>
                                Go to Measurements
                            </Button>
                            <Button onClick={() => navigate('/measurement-list')}>
                                View Measurement List
                            </Button>
                        </Space>
                    </Space>
                </Card>
            )}
        </div>
    );
    };

    // ── AI Analysis step render ───────────────────────────────────────────────

    const renderAiAnalysisStep = () => (
        <div className="mi-step-content">
            {/* Truncation warning — file was too large for AI to process all rows */}
            {aiTruncated && (
                <Alert
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    message="Output was truncated — file too large"
                    description="The file contained more data than could fit in a single AI response. Some locations were omitted. To import the full dataset, split the file into smaller chunks or use Manual import mode."
                    style={{ marginBottom: 12 }}
                    closable
                />
            )}
            {/* Parse error — AI response was not valid JSON */}
            {aiParseError && (
                <Alert
                    type="error"
                    showIcon
                    message="AI response could not be parsed"
                    description={`${aiParseError}. Try asking the AI to "reformat as valid JSON" or re-upload a smaller file.`}
                    style={{ marginBottom: 12 }}
                    closable
                    onClose={() => setAiParseError(null)}
                />
            )}
            <Card size="small" title={<Space><span>🤖</span> AI Analysis</Space>} className="mi-ai-chat-card">
                <div className="mi-ai-chat-history">
                    {aiChatHistory.map((msg, i) => (
                        <div key={i} className={`mi-ai-msg mi-ai-msg-${msg.role}`}>
                            <span className="mi-ai-msg-icon">{msg.role === 'ai' ? '🤖' : '👤'}</span>
                            <Text style={{ fontSize: 13 }}>{msg.text}</Text>
                        </div>
                    ))}
                    {aiLoading && (
                        <div className="mi-ai-msg mi-ai-msg-ai">
                            <span className="mi-ai-msg-icon">🤖</span>
                            <Text type="secondary" style={{ fontSize: 13 }}>Thinking…</Text>
                        </div>
                    )}
                </div>
                <Divider style={{ margin: '8px 0' }} />
                <Space.Compact style={{ width: '100%' }}>
                    <Input
                        value={aiChatInput}
                        onChange={(e) => setAiChatInput(e.target.value)}
                        onPressEnter={handleAiRefine}
                        placeholder='Refine — e.g. "Remove temperature", "Group by location and depth"'
                        disabled={aiLoading}
                    />
                    <Button type="primary" onClick={handleAiRefine} loading={aiLoading} disabled={!aiChatInput.trim()}>
                        Send
                    </Button>
                </Space.Compact>
            </Card>

            {aiMeasurements.length > 0 && (
                <Card
                    size="small"
                    className="mi-review-card"
                    title={<Space>📍 Extracted Measurements <Tag color="purple">{aiMeasurements.length}</Tag></Space>}
                >
                    <div className="mi-table-scroll">
                        <Table
                            size="small"
                            dataSource={aiMeasurements.slice(0, 100).map((m: any, i: number) => ({ ...m, _key: i }))}
                            rowKey="_key"
                            pagination={{ pageSize: 15, showSizeChanger: false }}
                            scroll={{ x: true }}
                            columns={[
                                {
                                    title: 'Latitude', dataIndex: 'latitude', width: 110,
                                    render: (v: number) => v != null ? v.toFixed(6) : <Tag color="error">?</Tag>
                                },
                                {
                                    title: 'Longitude', dataIndex: 'longitude', width: 110,
                                    render: (v: number) => v != null ? v.toFixed(6) : <Tag color="error">?</Tag>
                                },
                                {
                                    title: 'Date', dataIndex: 'measuredAt', width: 130,
                                    render: (v: string | null) => v ? new Date(v).toLocaleDateString() : <Tag>—</Tag>
                                },
                                {
                                    title: 'Code', dataIndex: 'measurementCode',
                                    render: (v: string | null) => v || <Tag color="blue">Auto</Tag>
                                },
                                {
                                    title: 'Values', dataIndex: 'values',
                                    render: (vals: any[]) => (
                                        <Space wrap size={2}>
                                            {(vals || []).map((v: any, idx: number) => (
                                                <Tag key={idx} color="geekblue" style={{ fontSize: 11 }}>
                                                    {v.valueType}: {v.numValue ?? v.textValue ?? '—'}
                                                </Tag>
                                            ))}
                                        </Space>
                                    )
                                }
                            ]}
                        />
                    </div>
                </Card>
            )}
        </div>
    );

    // ── Steps config ─────────────────────────────────────────────────────────

    const stepsConfig = importMode === 'ai' ? [
        { title: 'Upload', description: 'Select file' },
        { title: 'AI Analysis', description: 'Review & refine' },
        { title: 'Device & Area', description: 'Select assignments' },
        { title: 'Review & Import', description: 'Confirm and submit' },
    ] : [
        { title: 'Upload', description: 'Select & parse file' },
        { title: 'Mapping', description: 'Assign column roles' },
        { title: 'Device & Area', description: 'Select assignments' },
        { title: 'Review & Import', description: 'Confirm and submit' },
    ];

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="mi-page">
            <div className="mi-header">
                <Space>
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate('/measurements')}
                        type="text"
                    />
                    <Title level={4} style={{ margin: 0 }}>
                        <ImportOutlined style={{ marginRight: 8 }} />
                        Import Manual Measurements
                    </Title>
                </Space>
            </div>

            <Card className="mi-wizard-card">
                {/* ── Import mode toggle — always visible at the top ── */}
                {currentStep === 'upload' && (
                    <div className="mi-mode-toggle">
                        <Text type="secondary" style={{ marginRight: 12, fontSize: 13 }}>Import mode:</Text>
                        <Button
                            type={importMode === 'manual' ? 'primary' : 'default'}
                            onClick={() => { setImportMode('manual'); setCurrentStep('upload'); }}
                            style={{ marginRight: 8 }}
                        >
                            📋 Manual — map columns yourself
                        </Button>
                        <Button
                            type={importMode === 'ai' ? 'primary' : 'default'}
                            style={importMode === 'ai' ? { background: '#722ed1', borderColor: '#722ed1' } : {}}
                            onClick={() => { setImportMode('ai'); setCurrentStep('upload'); }}
                        >
                            🤖 AI Import — reads any file
                        </Button>
                    </div>
                )}

                <Steps
                    current={currentStepIndex}
                    size="small"
                    className="mi-steps"
                    items={stepsConfig.map((s, i) => ({
                        title: s.title,
                        description: s.description,
                        status: i < currentStepIndex ? 'finish' : i === currentStepIndex ? 'process' : 'wait',
                    }))}
                />

                <Divider style={{ margin: '16px 0' }} />

                <div className="mi-step-body">
                    {currentStep === 'upload' && renderUploadStep()}
                    {currentStep === 'mapping' && renderMappingStep()}
                    {currentStep === 'ai_analysis' && renderAiAnalysisStep()}
                    {currentStep === 'assignment' && renderAssignmentStep()}
                    {currentStep === 'review' && renderReviewStep()}
                </div>

                <Divider style={{ margin: '16px 0' }} />

                <div className="mi-footer">
                    <Button
                        onClick={goBack}
                        disabled={currentStepIndex === 0 || importing}
                        icon={<ArrowLeftOutlined />}
                    >
                        Back
                    </Button>
                    <Space>
                        <Button onClick={() => navigate('/measurements')} disabled={importing}>
                            Cancel
                        </Button>
                        {currentStep !== 'review' ? (
                            <Button
                                type="primary"
                                loading={importMode === 'ai' && currentStep === 'upload' && aiLoading}
                                onClick={goNext}
                                style={importMode === 'ai' && currentStep === 'upload' ? { background: '#722ed1', borderColor: '#722ed1' } : {}}
                                disabled={
                                    (currentStep === 'upload' && !canProceedFromUpload) ||
                                    (currentStep === 'mapping' && !canProceedFromMapping) ||
                                    (currentStep === 'assignment' && !canProceedFromAssignment) ||
                                    (currentStep === 'ai_analysis' && !canProceedFromAssignment)
                                }
                            >
                                {importMode === 'ai' && currentStep === 'upload' ? '🤖 Analyse with AI' : 'Next →'}
                            </Button>
                        ) : (
                            !importResult && (
                                <Button
                                    type="primary"
                                    icon={<ImportOutlined />}
                                    loading={importing}
                                    onClick={doImport}
                                    disabled={activeImportCount === 0}
                                >
                                    Import {activeImportCount} Measurement{activeImportCount !== 1 ? 's' : ''}
                                </Button>
                            )
                        )}
                    </Space>
                </div>
            </Card>
        </div>
    );
};

export default MeasurementImport;
